import React from 'react';

import { MeQuery } from '@/graphql';

export enum PersonActionKind {
  Authenticate = 'AUTHENTICATE',
  Authorize = 'AUTHORIZE',
  ChangeLocale = 'CHANGE_LOCALE',
  ChangeDateFormat = 'CHANGE_DATE_FORMAT',
  Change24HFormat = 'CHANGE_24H_FORMAT',
  SetAcknowledgements = 'SET_ACKNOWLEDGEMENTS',
  Reset = 'RESET',
}

type Person = NonNullable<MeQuery['me']>;

type Action =
  | {
      type: PersonActionKind.Authenticate;
      payload: { ppnuid: string } | { token: string };
    }
  | {
      type: PersonActionKind.Authorize;
      payload: Person;
    }
  | {
      type: PersonActionKind.ChangeLocale;
      payload: string;
    }
  | {
      type: PersonActionKind.Change24HFormat;
      payload: boolean;
    }
  | {
      type: PersonActionKind.ChangeDateFormat;
      payload: string;
    }
  | {
      type: PersonActionKind.SetAcknowledgements;
      payload: {
        userDeletionConsent?: string;
        emailNotificationsHint?: {
          DASHBOARD?: string | null;
        } | null;
      };
    }
  | {
      type: PersonActionKind.Reset;
    };

type Dispatch = (action: Action) => void;
type PersonProviderProps = { children: React.ReactNode };
type State = {
  isInitializing: boolean;
  isAuthorized: boolean;
  isAuthenticated: boolean;
  token?: string;
  isAnonymous?: boolean;
  ppnuid?: string;
  mail?: string;
  roles?: string[];
  organization?: {
    name: string;
  };
  settings?: {
    locale?: string | null;
    dateFormat?: string | null;
    is24HourFormat?: boolean | null;
  } | null;
  acknowledgements?: {
    userDeletionConsent?: string | null;
    emailNotificationsHint?: {
      DASHBOARD?: string | null;
    } | null;
  } | null;
};

const PersonContext = React.createContext<
  { state: State; dispatch: Dispatch } | undefined
>(undefined);

const personReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case PersonActionKind.Authenticate: {
      return {
        ppnuid: 'ppnuid' in action.payload ? action.payload.ppnuid : undefined,
        token: 'token' in action.payload ? action.payload.token : undefined,
        isInitializing: false,
        isAuthenticated:
          'ppnuid' in action.payload || 'token' in action.payload,
        isAuthorized: false,
        isAnonymous: 'token' in action.payload,
      };
    }
    case PersonActionKind.Authorize: {
      return {
        ...action.payload,
        isInitializing: false,
        isAuthorized:
          !('roles' in action.payload) || action.payload.roles.length > 0,
        isAuthenticated: true,
        isAnonymous: action.payload.__typename === 'AnonymousPerson',
      };
    }
    case PersonActionKind.ChangeLocale: {
      return {
        ...state,
        settings: {
          ...('settings' in state ? state.settings : {}),
          locale: action.payload,
        },
      };
    }
    case PersonActionKind.ChangeDateFormat: {
      return {
        ...state,
        settings: {
          ...state.settings,
          dateFormat: action.payload,
        },
      };
    }
    case PersonActionKind.Change24HFormat: {
      return {
        ...state,
        settings: {
          ...state.settings,
          is24HourFormat: action.payload,
        },
      };
    }
    case PersonActionKind.SetAcknowledgements: {
      return {
        ...state,
        acknowledgements: {
          ...state.acknowledgements,
          ...action.payload,
        },
      };
    }
    case PersonActionKind.Reset: {
      return {
        isInitializing: false,
        isAuthorized: false,
        isAuthenticated: false,
        isAnonymous: false,
      };
    }
    default: {
      throw new Error('Unhandled action type');
    }
  }
};

const PersonProvider = ({ children }: PersonProviderProps) => {
  const [state, dispatch] = React.useReducer(personReducer, {
    isInitializing: true,
    isAuthenticated: false,
    isAuthorized: false,
  });
  const value = { state, dispatch };
  return (
    <PersonContext.Provider value={value}>{children}</PersonContext.Provider>
  );
};

const usePersonContext = () => {
  const context = React.useContext(PersonContext);
  if (context === undefined) {
    throw new Error('usePersonContext must be used within a PersonContext');
  }
  return context;
};

export { PersonProvider, usePersonContext };
